import type { Field, Option } from '../types/form';

const formatDate = (
  dateString: string,
): {
  dateOfMonth: string;
  month: string;
  year: string;
} => {
  const date = new Date(dateString);
  const dateOfMonth = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const year = `${date.getFullYear()}`;

  return {
    dateOfMonth,
    month,
    year,
  };
};

const formatIsoDate = (dateString: string | undefined | null): string => {
  if (dateString == null || dateString.length === 0) {
    return '';
  }

  const date = formatDate(dateString);
  return `${date.year}-${date.month}-${date.dateOfMonth}`;
};

const formatGermanDate = (dateString: string | undefined | null): string => {
  if (dateString == null || dateString.length === 0) {
    return '';
  }

  const date = formatDate(dateString);
  return `${date.dateOfMonth}.${date.month}.${date.year}`;
};

const formatPhoneNumber = (phoneString: string | undefined | null): string => {
  if (phoneString == null || phoneString.length === 0) {
    return '';
  }

  const prefix = phoneString.slice(0, 4);
  const number = phoneString.slice(4);

  return `${prefix} ${number}`;
};

const getFieldSelectOptions = <T>(fieldKey: string, fields: Field<T>[]): Option[] => {
  const field = fields.find((field) => field.key === fieldKey);
  return field != null && (field.fieldTypes === 'select' || field.fieldTypes === 'checkboxSelect') ? field.options : [];
};

const getOptionLabel = (options: Option[], optionKey?: string): string => {
  if (optionKey == null) {
    return '';
  }
  const optionToKey = options.find((option) => option.value === optionKey);
  return optionToKey != null ? optionToKey.label : '';
};

const getYearRange = (startDateAsString: string, endDateAsString?: string): number | null => {
  if (isNaN(Date.parse(startDateAsString)) || (endDateAsString != null && isNaN(Date.parse(endDateAsString)))) {
    return null;
  }
  const startDate = new Date(startDateAsString);
  const endDate = endDateAsString != null ? new Date(endDateAsString) : new Date();
  const age = endDate.getFullYear() - startDate.getFullYear();
  const m = endDate.getMonth() - startDate.getMonth();
  const exactAge = m < 0 || (m === 0 && endDate.getDate() < startDate.getDate()) ? age - 1 : age;

  return exactAge;
};

const getDaysBetweenTwoDates = (startDate?: string, endDate?: string): number => {
  if (startDate === undefined || endDate === undefined) {
    return 0;
  }
  const startDateTime = new Date(startDate).getTime();
  const endDateTime = new Date(endDate).getTime();
  const diffTime = Math.abs(endDateTime - startDateTime);
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

const formatter = {
  formatIsoDate,
  formatGermanDate,
  formatPhoneNumber,
  getFieldSelectOptions,
  getOptionLabel,
  getYearRange,
  getDaysBetweenTwoDates,
};

export default formatter;
